import React, { useEffect, useState, Component } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MultiStepForm } from '../components/MultiStepForm';
import { motion } from 'framer-motion';
import { UploadIcon, ImageIcon, DollarSignIcon, TagIcon, CheckIcon, InfoIcon, ChevronLeftIcon, PlusIcon, XIcon, LinkIcon, UserIcon, MailIcon, GlobeIcon, WalletIcon, FileIcon, MonitorIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface FormData {
  // Step 1: Product Info
  productName: string;
  shortDescription: string;
  coverImage: File | null;
  category: string;
  // Step 2: Upload Files
  mainFile: File | null;
  mainFileLink: string;
  previewImages: File[];
  demoLink: string;
  // Step 3: Details
  platforms: string[];
  price: string;
  isFree: boolean;
  licenseType: string;
  tags: string[];
  // Step 4: Creator Info
  sellerName: string;
  portfolioUrl: string;
  email: string;
  paymentWallet: string;
  acceptTerms: boolean;
}
export const SubmitProductPage: React.FC = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [currentTag, setCurrentTag] = useState('');
  const [currentPlatform, setCurrentPlatform] = useState('');
  const [submissionComplete, setSubmissionComplete] = useState(false);
  // Form data state
  const [formData, setFormData] = useState<FormData>({
    // Step 1: Product Info
    productName: '',
    shortDescription: '',
    coverImage: null,
    category: '',
    // Step 2: Upload Files
    mainFile: null,
    mainFileLink: '',
    previewImages: [],
    demoLink: '',
    // Step 3: Details
    platforms: [],
    price: '',
    isFree: false,
    licenseType: '',
    tags: [],
    // Step 4: Creator Info
    sellerName: '',
    portfolioUrl: '',
    email: '',
    paymentWallet: '',
    acceptTerms: false
  });
  // Preview states
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [previewImagePreviews, setPreviewImagePreviews] = useState<string[]>([]);
  // Form validation states
  const [step1Valid, setStep1Valid] = useState(false);
  const [step2Valid, setStep2Valid] = useState(false);
  const [step3Valid, setStep3Valid] = useState(false);
  const [step4Valid, setStep4Valid] = useState(false);
  // Error states
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Categories
  const categories = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Shorts Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates'];
  // License types
  const licenseTypes = ['Personal Use', 'Commercial Use', 'Extended Commercial'];
  // Platform suggestions
  const platformSuggestions = ['React', 'HTML/CSS', 'Figma', 'Webflow', 'WordPress', 'Shopify', 'Notion', 'Framer', 'After Effects', 'Photoshop', 'Illustrator', 'Mobile iOS', 'Mobile Android'];
  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Validate each step
  useEffect(() => {
    // Step 1 validation
    const validateStep1 = () => {
      const step1Errors: Record<string, string> = {};
      if (!formData.productName.trim()) {
        step1Errors.productName = 'Product name is required';
      }
      if (!formData.shortDescription.trim()) {
        step1Errors.shortDescription = 'Short description is required';
      } else if (formData.shortDescription.length > 120) {
        step1Errors.shortDescription = 'Short description must be 120 characters or less';
      }
      if (!formData.coverImage) {
        step1Errors.coverImage = 'Cover image is required';
      }
      if (!formData.category) {
        step1Errors.category = 'Category is required';
      }
      setErrors(prev => ({
        ...prev,
        ...step1Errors
      }));
      setStep1Valid(Object.keys(step1Errors).length === 0);
    };
    // Step 2 validation
    const validateStep2 = () => {
      const step2Errors: Record<string, string> = {};
      if (!formData.mainFile && !formData.mainFileLink.trim()) {
        step2Errors.mainFile = 'Either upload a file or provide a link';
      }
      if (formData.previewImages.length === 0) {
        step2Errors.previewImages = 'At least one preview image is required';
      }
      setErrors(prev => ({
        ...prev,
        ...step2Errors
      }));
      setStep2Valid(Object.keys(step2Errors).length === 0);
    };
    // Step 3 validation
    const validateStep3 = () => {
      const step3Errors: Record<string, string> = {};
      if (formData.platforms.length === 0) {
        step3Errors.platforms = 'At least one platform is required';
      }
      if (!formData.isFree && (!formData.price.trim() || isNaN(Number(formData.price)) || Number(formData.price) <= 0)) {
        step3Errors.price = 'Please enter a valid price';
      }
      if (!formData.licenseType) {
        step3Errors.licenseType = 'License type is required';
      }
      if (formData.tags.length === 0) {
        step3Errors.tags = 'At least one tag is required';
      }
      setErrors(prev => ({
        ...prev,
        ...step3Errors
      }));
      setStep3Valid(Object.keys(step3Errors).length === 0);
    };
    // Step 4 validation
    const validateStep4 = () => {
      const step4Errors: Record<string, string> = {};
      if (!formData.sellerName.trim()) {
        step4Errors.sellerName = 'Seller name is required';
      }
      if (!formData.email.trim()) {
        step4Errors.email = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        step4Errors.email = 'Please enter a valid email';
      }
      if (formData.portfolioUrl.trim() && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.portfolioUrl)) {
        step4Errors.portfolioUrl = 'Please enter a valid URL';
      }
      if (!formData.acceptTerms) {
        step4Errors.acceptTerms = 'You must accept the terms and guidelines';
      }
      setErrors(prev => ({
        ...prev,
        ...step4Errors
      }));
      setStep4Valid(Object.keys(step4Errors).length === 0);
    };
    validateStep1();
    validateStep2();
    validateStep3();
    validateStep4();
  }, [formData]);
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  // Handle file uploads
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'coverImage' | 'mainFile' | 'previewImages') => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (fileType === 'coverImage') {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        coverImage: file
      }));
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      // Clear error
      if (errors.coverImage) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.coverImage;
          return newErrors;
        });
      }
    } else if (fileType === 'mainFile') {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        mainFile: file
      }));
      // Clear error
      if (errors.mainFile) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.mainFile;
          return newErrors;
        });
      }
    } else if (fileType === 'previewImages') {
      const files = Array.from(e.target.files);
      setFormData(prev => ({
        ...prev,
        previewImages: [...prev.previewImages, ...files]
      }));
      // Create preview URLs
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
      // Clear error
      if (errors.previewImages) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.previewImages;
          return newErrors;
        });
      }
    }
  };
  // Handle removing preview images
  const handleRemovePreviewImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      previewImages: prev.previewImages.filter((_, i) => i !== index)
    }));
    setPreviewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };
  // Handle adding tags
  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
      // Clear error
      if (errors.tags) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.tags;
          return newErrors;
        });
      }
    }
  };
  // Handle removing tags
  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  // Handle adding platforms
  const handleAddPlatform = () => {
    if (currentPlatform.trim() && !formData.platforms.includes(currentPlatform.trim())) {
      setFormData(prev => ({
        ...prev,
        platforms: [...prev.platforms, currentPlatform.trim()]
      }));
      setCurrentPlatform('');
      // Clear error
      if (errors.platforms) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.platforms;
          return newErrors;
        });
      }
    }
  };
  // Handle removing platforms
  const handleRemovePlatform = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.filter(p => p !== platform)
    }));
  };
  // Handle selecting a suggested platform
  const handleSelectPlatform = (platform: string) => {
    if (!formData.platforms.includes(platform)) {
      setFormData(prev => ({
        ...prev,
        platforms: [...prev.platforms, platform]
      }));
      // Clear error
      if (errors.platforms) {
        setErrors(prev => {
          const newErrors = {
            ...prev
          };
          delete newErrors.platforms;
          return newErrors;
        });
      }
    }
  };
  // Handle toggling free product
  const handleToggleFree = (isFree: boolean) => {
    setFormData(prev => ({
      ...prev,
      isFree,
      price: isFree ? '0' : prev.price
    }));
  };
  // Handle form submission
  const handleFormSubmit = () => {
    // Simulate form submission
    console.log('Form submitted:', formData);
    setSubmissionComplete(true);
  };
  // Step 1: Product Info
  const ProductInfoStep = <div data-id="element-7068">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-7069">
        Product Information
      </h2>
      <div className="space-y-6" data-id="element-7070">
        <div data-id="element-7071">
          <label htmlFor="productName" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7072">
            Product Name *
          </label>
          <input type="text" id="productName" name="productName" value={formData.productName} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.productName ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="E.g. Premium Website Template" data-id="element-7073" />
          {errors.productName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7074">
              {errors.productName}
            </p>}
        </div>
        <div data-id="element-7075">
          <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7076">
            Short Description *
          </label>
          <textarea id="shortDescription" name="shortDescription" value={formData.shortDescription} onChange={handleChange} rows={3} className={`w-full px-4 py-2 rounded-lg border ${errors.shortDescription ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Brief description of your product (max 120 characters)" maxLength={120} data-id="element-7077" />
          <div className="flex justify-between items-center mt-1" data-id="element-7078">
            {errors.shortDescription ? <p className="text-sm text-red-600 dark:text-red-400" data-id="element-7079">
                {errors.shortDescription}
              </p> : <span className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7080">
                This will appear in product cards and search results
              </span>}
            <span className={`text-xs ${formData.shortDescription.length > 100 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-7081">
              {formData.shortDescription.length}/120
            </span>
          </div>
        </div>
        <div data-id="element-7082">
          <label htmlFor="coverImage" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7083">
            Cover Image *
          </label>
          <div className="mt-1 flex items-center" data-id="element-7084">
            <div className={`flex justify-center items-center w-40 h-40 rounded-lg border-2 border-dashed ${errors.coverImage ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors cursor-pointer overflow-hidden relative`} data-id="element-7085">
              {coverImagePreview ? <img src={coverImagePreview} alt="Cover preview" className="w-full h-full object-cover" data-id="element-7086" /> : <div className="text-center p-4" data-id="element-7087">
                  <ImageIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mx-auto mb-1" data-id="element-7088" />
                  <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7089">
                    Upload image
                  </p>
                </div>}
              <input type="file" id="coverImage" name="coverImage" accept="image/*" onChange={e => handleFileChange(e, 'coverImage')} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-7090" />
            </div>
            <div className="ml-5 text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-7091">
              <p data-id="element-7092">Recommended size: 1200×800px</p>
              <p data-id="element-7093">Max file size: 5MB</p>
              <p data-id="element-7094">Format: JPG, PNG</p>
            </div>
          </div>
          {errors.coverImage && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7095">
              {errors.coverImage}
            </p>}
        </div>
        <div data-id="element-7096">
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7097">
            Category *
          </label>
          <select id="category" name="category" value={formData.category} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.category ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-7098">
            <option value="" disabled data-id="element-7099">
              Select a category
            </option>
            {categories.map(category => <option key={category} value={category} data-id="element-7100">
                {category}
              </option>)}
          </select>
          {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7101">
              {errors.category}
            </p>}
        </div>
      </div>
    </div>;
  // Step 2: Upload Files
  const UploadFilesStep = <div data-id="element-7102">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-7103">
        Upload Files
      </h2>
      <div className="space-y-6" data-id="element-7104">
        <div data-id="element-7105">
          <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7106">
            Main Product File or Link *
          </label>
          <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-6 border border-gray-200 dark:border-[#1A3330]" data-id="element-7107">
            <div className="flex flex-col space-y-4" data-id="element-7108">
              {/* Upload file option */}
              <div data-id="element-7109">
                <p className="text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-2" data-id="element-7110">
                  Upload ZIP, Figma, MP4, etc.
                </p>
                <div className={`flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed ${errors.mainFile && !formData.mainFileLink ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors cursor-pointer relative`} data-id="element-7111">
                  {formData.mainFile ? <div className="flex items-center" data-id="element-7112">
                      <FileIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A] mr-2" data-id="element-7113" />
                      <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-7114">
                        {formData.mainFile.name}
                      </span>
                    </div> : <div className="text-center p-4" data-id="element-7115">
                      <UploadIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mx-auto mb-1" data-id="element-7116" />
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7117">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-7118">
                        Max size: 1GB
                      </p>
                    </div>}
                  <input type="file" id="mainFile" name="mainFile" onChange={e => handleFileChange(e, 'mainFile')} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-7119" />
                </div>
              </div>
              <div className="flex items-center" data-id="element-7120">
                <div className="flex-grow h-px bg-gray-200 dark:bg-[#1A3330]" data-id="element-7121"></div>
                <span className="px-4 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7122">
                  OR
                </span>
                <div className="flex-grow h-px bg-gray-200 dark:bg-[#1A3330]" data-id="element-7123"></div>
              </div>
              {/* Link option */}
              <div data-id="element-7124">
                <p className="text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-2" data-id="element-7125">
                  Provide a link (Figma, Notion, Drive, etc.)
                </p>
                <div className="relative" data-id="element-7126">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7127">
                    <LinkIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7128" />
                  </div>
                  <input type="text" id="mainFileLink" name="mainFileLink" value={formData.mainFileLink} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.mainFile && !formData.mainFile ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="https://figma.com/file/..." data-id="element-7129" />
                </div>
              </div>
            </div>
          </div>
          {errors.mainFile && !formData.mainFile && !formData.mainFileLink && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7130">
              {errors.mainFile}
            </p>}
        </div>
        <div data-id="element-7131">
          <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7132">
            Preview Screenshots *
          </label>
          <div className="space-y-4" data-id="element-7133">
            {/* Preview images grid */}
            {previewImagePreviews.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4" data-id="element-7134">
                {previewImagePreviews.map((preview, index) => <div key={index} className="relative group" data-id="element-7135">
                    <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-[#1A3330]" data-id="element-7136" />
                    <button type="button" onClick={() => handleRemovePreviewImage(index)} className="absolute top-2 right-2 bg-white dark:bg-[#162825] rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7137">
                      <XIcon className="h-4 w-4 text-gray-500 dark:text-[#7A8F8A]" data-id="element-7138" />
                    </button>
                  </div>)}
              </div>}
            {/* Upload more images */}
            <div className={`flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed ${errors.previewImages ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors cursor-pointer relative`} data-id="element-7139">
              <div className="text-center p-4" data-id="element-7140">
                <ImageIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mx-auto mb-1" data-id="element-7141" />
                <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-7142">
                  {previewImagePreviews.length > 0 ? 'Add more screenshots' : 'Upload preview screenshots'}
                </p>
                <p className="text-xs text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-7143">
                  Max: 5 images
                </p>
              </div>
              <input type="file" id="previewImages" name="previewImages" accept="image/*" multiple onChange={e => handleFileChange(e, 'previewImages')} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={previewImagePreviews.length >= 5} data-id="element-7144" />
            </div>
          </div>
          {errors.previewImages && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7145">
              {errors.previewImages}
            </p>}
        </div>
        <div data-id="element-7146">
          <label htmlFor="demoLink" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7147">
            Demo Link (Optional)
          </label>
          <div className="relative" data-id="element-7148">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7149">
              <LinkIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7150" />
            </div>
            <input type="text" id="demoLink" name="demoLink" value={formData.demoLink} onChange={handleChange} className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="https://demo.example.com" data-id="element-7151" />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7152">
            If available, provide a link where buyers can preview your product
          </p>
        </div>
      </div>
    </div>;
  // Step 3: Details
  const DetailsStep = <div data-id="element-7153">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-7154">
        Product Details
      </h2>
      <div className="space-y-6" data-id="element-7155">
        <div data-id="element-7156">
          <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7157">
            Platform Compatibility *
          </label>
          <div className="space-y-3" data-id="element-7158">
            {/* Selected platforms */}
            {formData.platforms.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-7159">
                {formData.platforms.map(platform => <div key={platform} className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-7160">
                    <span data-id="element-7161">{platform}</span>
                    <button type="button" onClick={() => handleRemovePlatform(platform)} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-7162">
                      <XIcon className="h-3 w-3" data-id="element-7163" />
                    </button>
                  </div>)}
              </div>}
            {/* Platform input */}
            <div className="flex" data-id="element-7164">
              <input type="text" value={currentPlatform} onChange={e => setCurrentPlatform(e.target.value)} className={`flex-grow px-4 py-2 rounded-l-lg border ${errors.platforms ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Add platform (e.g., React, Figma)" onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddPlatform();
              }
            }} data-id="element-7165" />
              <button type="button" onClick={handleAddPlatform} className="px-4 py-2 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-r-lg border border-l-0 border-gray-300 dark:border-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#0B2C2A]" data-id="element-7166">
                Add
              </button>
            </div>
            {errors.platforms && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7167">
                {errors.platforms}
              </p>}
            {/* Platform suggestions */}
            <div data-id="element-7168">
              <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mb-2" data-id="element-7169">
                Suggested platforms:
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-7170">
                {platformSuggestions.map(platform => <button key={platform} type="button" onClick={() => handleSelectPlatform(platform)} className={`text-xs px-3 py-1 rounded-full ${formData.platforms.includes(platform) ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0] cursor-not-allowed' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-200 dark:hover:bg-[#0B2C2A]'}`} disabled={formData.platforms.includes(platform)} data-id="element-7171">
                    {platform}
                  </button>)}
              </div>
            </div>
          </div>
        </div>
        <div data-id="element-7172">
          <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7173">
            Price *
          </label>
          <div className="space-y-3" data-id="element-7174">
            {/* Free or Paid toggle */}
            <div className="flex space-x-4" data-id="element-7175">
              <button type="button" onClick={() => handleToggleFree(true)} className={`px-4 py-2 rounded-lg text-sm font-medium ${formData.isFree ? 'bg-[#2DB489] dark:bg-[#4ED1A0] text-white' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-7176">
                Free
              </button>
              <button type="button" onClick={() => handleToggleFree(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${!formData.isFree ? 'bg-[#2DB489] dark:bg-[#4ED1A0] text-white' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-7177">
                Paid
              </button>
            </div>
            {/* Price input (shown only if paid) */}
            {!formData.isFree && <div className="relative" data-id="element-7178">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7179">
                  <DollarSignIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7180" />
                </div>
                <input type="text" id="price" name="price" value={formData.price} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.price ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="29.99" data-id="element-7181" />
                {errors.price && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7182">
                    {errors.price}
                  </p>}
                <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7183">
                  You'll receive 80% of the sale price
                </p>
              </div>}
          </div>
        </div>
        <div data-id="element-7184">
          <label htmlFor="licenseType" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7185">
            License Type *
          </label>
          <select id="licenseType" name="licenseType" value={formData.licenseType} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.licenseType ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-7186">
            <option value="" disabled data-id="element-7187">
              Select a license type
            </option>
            {licenseTypes.map(license => <option key={license} value={license} data-id="element-7188">
                {license}
              </option>)}
          </select>
          {errors.licenseType && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7189">
              {errors.licenseType}
            </p>}
        </div>
        <div data-id="element-7190">
          <label className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7191">
            Tags *
          </label>
          <div className="space-y-3" data-id="element-7192">
            {/* Selected tags */}
            {formData.tags.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-7193">
                {formData.tags.map(tag => <div key={tag} className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-7194">
                    <span data-id="element-7195">{tag}</span>
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-7196">
                      <XIcon className="h-3 w-3" data-id="element-7197" />
                    </button>
                  </div>)}
              </div>}
            {/* Tag input */}
            <div className="flex" data-id="element-7198">
              <input type="text" value={currentTag} onChange={e => setCurrentTag(e.target.value)} className={`flex-grow px-4 py-2 rounded-l-lg border ${errors.tags ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Add a tag (e.g., responsive, dark mode)" onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }} data-id="element-7199" />
              <button type="button" onClick={handleAddTag} className="px-4 py-2 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-r-lg border border-l-0 border-gray-300 dark:border-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#0B2C2A]" data-id="element-7200">
                Add
              </button>
            </div>
            {errors.tags && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7201">
                {errors.tags}
              </p>}
            <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7202">
              Press Enter or click Add to add a tag
            </p>
          </div>
        </div>
      </div>
    </div>;
  // Step 4: Creator Info
  const CreatorInfoStep = <div data-id="element-7203">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-7204">
        Creator Information
      </h2>
      <div className="space-y-6" data-id="element-7205">
        <div data-id="element-7206">
          <label htmlFor="sellerName" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7207">
            Seller Name *
          </label>
          <div className="relative" data-id="element-7208">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7209">
              <UserIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7210" />
            </div>
            <input type="text" id="sellerName" name="sellerName" value={formData.sellerName} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.sellerName ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Your name or company name" data-id="element-7211" />
          </div>
          {errors.sellerName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7212">
              {errors.sellerName}
            </p>}
        </div>
        <div data-id="element-7213">
          <label htmlFor="portfolioUrl" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7214">
            Portfolio URL (Optional)
          </label>
          <div className="relative" data-id="element-7215">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7216">
              <GlobeIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7217" />
            </div>
            <input type="text" id="portfolioUrl" name="portfolioUrl" value={formData.portfolioUrl} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.portfolioUrl ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="https://yourportfolio.com" data-id="element-7218" />
          </div>
          {errors.portfolioUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7219">
              {errors.portfolioUrl}
            </p>}
        </div>
        <div data-id="element-7220">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7221">
            Email *
          </label>
          <div className="relative" data-id="element-7222">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7223">
              <MailIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7224" />
            </div>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.email ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="you@example.com" data-id="element-7225" />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7226">
              {errors.email}
            </p>}
        </div>
        <div data-id="element-7227">
          <label htmlFor="paymentWallet" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-7228">
            Payment Wallet (Optional)
          </label>
          <div className="relative" data-id="element-7229">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7230">
              <WalletIcon className="h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-7231" />
            </div>
            <input type="text" id="paymentWallet" name="paymentWallet" value={formData.paymentWallet} onChange={handleChange} className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="PayPal email or crypto wallet address" data-id="element-7232" />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-7233">
            Where you'd like to receive payments. We'll contact you to set up
            payments.
          </p>
        </div>
        <div className="bg-blue-50 dark:bg-[#0B3C45]/30 border border-blue-100 dark:border-[#0B3C45] rounded-lg p-4 mb-6" data-id="element-7234">
          <div className="flex" data-id="element-7235">
            <InfoIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-7236" />
            <div data-id="element-7237">
              <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-1" data-id="element-7238">
                Product Guidelines
              </h3>
              <ul className="text-sm text-blue-700 dark:text-blue-200 space-y-1 list-disc list-inside" data-id="element-7239">
                <li data-id="element-7240">Products must be original and high-quality</li>
                <li data-id="element-7241">
                  No stolen content, AI-repackaged material, or copyrighted
                  assets
                </li>
                <li data-id="element-7242">
                  All products must include documentation or usage instructions
                </li>
                <li data-id="element-7243">Products must be fully functional and as described</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="flex items-start" data-id="element-7244">
          <div className="flex items-center h-5" data-id="element-7245">
            <input id="acceptTerms" name="acceptTerms" type="checkbox" checked={formData.acceptTerms} onChange={handleChange} className="focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-7246" />
          </div>
          <div className="ml-3" data-id="element-7247">
            <label htmlFor="acceptTerms" className={`text-sm ${errors.acceptTerms ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-[#C4D1CC]'}`} data-id="element-7248">
              I confirm this product follows all guidelines and is original *
            </label>
            {errors.acceptTerms && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-7249">
                {errors.acceptTerms}
              </p>}
          </div>
        </div>
      </div>
    </div>;
  // Form steps
  const formSteps = [{
    title: 'Product Info',
    content: ProductInfoStep,
    isValid: step1Valid
  }, {
    title: 'Upload Files',
    content: UploadFilesStep,
    isValid: step2Valid
  }, {
    title: 'Details',
    content: DetailsStep,
    isValid: step3Valid
  }, {
    title: 'Creator Info',
    content: CreatorInfoStep,
    isValid: step4Valid
  }];
  // Success message
  const SuccessMessage = <div className="text-center py-8" data-id="element-7250">
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-7251">
        <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-7252" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-[#F8FAF9] mb-4" data-id="element-7253">
        Product Submitted!
      </h2>
      <p className="text-gray-600 dark:text-[#B0C1BD] mb-8" data-id="element-7254">
        Your product has been submitted for review. We'll notify you once it's
        approved.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-7255">
        <button onClick={() => navigate('/seller-dashboard')} className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all dark-glow" data-id="element-7256">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-7257" />
          Go to Dashboard
        </button>
        <button onClick={() => {
        setFormData({
          productName: '',
          shortDescription: '',
          coverImage: null,
          category: '',
          mainFile: null,
          mainFileLink: '',
          previewImages: [],
          demoLink: '',
          platforms: [],
          price: '',
          isFree: false,
          licenseType: '',
          tags: [],
          sellerName: '',
          portfolioUrl: '',
          email: '',
          paymentWallet: '',
          acceptTerms: false
        });
        setCoverImagePreview(null);
        setPreviewImagePreviews([]);
        setSubmissionComplete(false);
      }} className="inline-flex items-center gap-2 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] py-3 px-6 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-all" data-id="element-7258">
          <PlusIcon className="h-5 w-5" data-id="element-7259" />
          Submit Another Product
        </button>
      </div>
    </div>;
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-7260">
      <Navbar scrolled={scrolled} data-id="element-7261" />
      <main className="pt-24 pb-20" data-id="element-7262">
        <div className="container mx-auto px-4 md:px-6" data-id="element-7263">
          <div className="max-w-4xl mx-auto" data-id="element-7264">
            <div className="mb-8 flex items-center" data-id="element-7265">
              <button onClick={() => navigate(-1)} className="inline-flex items-center text-gray-600 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] mr-4" data-id="element-7266">
                <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-7267" />
                Back
              </button>
              <div data-id="element-7268">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-7269">
                  Submit Your Product
                </h1>
                <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-7270">
                  Fill out the form below to submit your digital product for
                  review
                </p>
              </div>
            </div>
            {submissionComplete ? <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] p-8" data-id="element-7271">
                {SuccessMessage}
              </div> : <MultiStepForm steps={formSteps} onComplete={handleFormSubmit} submitButtonText="Submit Product" data-id="element-7272" />}
          </div>
        </div>
      </main>
      <Footer data-id="element-7273" />
    </div>;
};